import { binarySelectOptions, genderSelectOptions } from '@application/utils';
import type { AnimalTypeEnum } from '@gql/graphql';
import { GenderEnum } from '@gql/graphql';
import {
  InputDateField,
  InputField,
  InputFileField,
  InputLabel,
  InputRadioField,
  InputSelectField,
  InputWrapper,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { useCallback } from 'react';
import type { BuyerAnimalFormProps, BuyerAnimalFormValues } from '..';
import * as Styled from '../styled';

export const FormGeneral: FC<BuyerAnimalFormProps> = (props) => {
  const { translate } = useTranslation();
  const { values } = useFormikContext<BuyerAnimalFormValues>();

  const {
    mode,
    form,
    disabledFields,
    imageLabel = translate('common.inputs.animalImage.label'),
  } = props;

  const isDisabled = useCallback(
    (key: keyof Omit<BuyerAnimalFormValues, 'dogInfo'>) => {
      if (mode === 'view') return true;
      if (disabledFields?.[key] === true) return true;
      return false;
    },
    [disabledFields, mode],
  );

  return (
    <>
      <InputWrapper split>
        <InputLabel>{imageLabel}</InputLabel>
        <InputWrapper>
          <Styled.ImageWrapperContainer>
            <Styled.ImageWrapper>
              <InputFileField
                name="images[0]"
                accept="image/*, .heic, .heif"
                disabled={isDisabled('images')}
              />
            </Styled.ImageWrapper>
          </Styled.ImageWrapperContainer>
        </InputWrapper>
      </InputWrapper>

      <InputWrapper split last>
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.animalType.label')}</InputLabel>
          <InputSelectField<AnimalTypeEnum>
            name="type"
            disabled={isDisabled('type')}
            options={form.options.animalTypes}
          />
        </InputWrapper>
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.animalName.label')}</InputLabel>
          <InputField name="name" disabled={isDisabled('name')} />
        </InputWrapper>

        <InputWrapper split>
          <InputLabel required>{translate('common.inputs.gender.label')}</InputLabel>
          <InputRadioField<GenderEnum>
            name="gender"
            grow
            disabled={isDisabled('gender')}
            options={genderSelectOptions(translate, {
              showIcons: true,
              showText: false,
              showOther: false,
            })}
          />
        </InputWrapper>
        <InputWrapper split last>
          <InputLabel>{translate('common.inputs.sterilized.label')}</InputLabel>
          <InputRadioField<boolean>
            name="sterilized"
            grow
            disabled={isDisabled('sterilized')}
            options={binarySelectOptions(translate)}
          />
        </InputWrapper>
        {values.gender === GenderEnum.F && (
          <InputWrapper split>
            <InputLabel>{translate('common.inputs.calved.label')}</InputLabel>
            <InputRadioField<boolean>
              name="calved"
              grow
              disabled={isDisabled('calved')}
              options={binarySelectOptions(translate)}
            />
          </InputWrapper>
        )}
      </InputWrapper>

      <InputWrapper split>
        <InputLabel required>{translate('common.inputs.knownBirthday.label')}</InputLabel>
        <InputRadioField
          grow
          name="birthdayType"
          disabled={isDisabled('birthdayType')}
          options={[
            { value: 'DATE', label: translate('glossary.exactKnown') },
            { value: 'MONTH', label: translate('glossary.monthKnown') },
          ]}
        />
      </InputWrapper>
      <InputWrapper split>
        <InputLabel required>
          {translate(
            values.birthdayType === 'DATE'
              ? 'common.inputs.animalBirthday.label'
              : 'common.inputs.animalBirthdayMonth.label',
          )}
        </InputLabel>
        <InputDateField
          name="birthday"
          disableFutureDates
          calendarProps={{
            maxDetail: values.birthdayType === 'MONTH' ? 'year' : undefined,
            defaultView: 'decade',
            maxDate: new Date(),
          }}
          disabled={isDisabled('birthday')}
        />
      </InputWrapper>
    </>
  );
};
